import { push } from 'connected-react-router'
import { State } from 'index'
import { FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'styles/components/template.module.scss'
import { BlueButton, CautionButton, NavButton } from 'components/Button'
import { AlertCard, PointCardCard, PointCardPreviewCard } from 'components/Card'
import { Spacer } from 'components/Utility'
import { getBrandRef } from 'reducks/auth/selectors'
import { setPointCard } from 'repos/pointCard'
import { PointCardType as BasePointCardType } from 'types/pointCard'
import { openCautionModal } from 'reducks/modal/operations'
import { fetchPointCard } from 'reducks/pointCard/operations'
import { getPointCard } from 'reducks/pointCard/selectors'
import { useFetchPointCardOffers, useFetchPointCardUsers } from 'hooks/pointCard'
import { initPointCardAction } from 'reducks/pointCard/actions'

type PointCardType = BasePointCardType & { ref: FirebaseDocRefType }

const PointCardTemplate = () => {
  const dispatch = useDispatch()
  const brandRef = useSelector((state: State) => getBrandRef(state))
  const pointCard = useSelector((state: State) => getPointCard(state))

  const { pointCardOffers } = useFetchPointCardOffers()
  const { pointCardUsers } = useFetchPointCardUsers()

  const handleEdit = () => {
    dispatch(push(`/point_card/${pointCard?.ref?.id ?? 'create'}`))
  }

  const handlePublishPointCard = async () => {
    if (!pointCard) return
    const nextState: PointCardType = {
      ...pointCard,
      status: 'active',
      updated_at: FirebaseTimestamp.now(),
    }
    await setPointCard(nextState)
    await dispatch(fetchPointCard(brandRef))
  }

  const handleSuspendPointCard = async () => {
    if (!pointCard) return
    const nextState: PointCardType = {
      ...pointCard,
      status: 'suspended',
      updated_at: FirebaseTimestamp.now(),
    }
    await setPointCard(nextState)
    await dispatch(fetchPointCard(brandRef))
  }

  const handleDeletePointCard = async () => {
    if (!pointCard) return
    const nextState: PointCardType = {
      ...pointCard,
      status: 'deleted',
      updated_at: FirebaseTimestamp.now(),
    }
    await setPointCard(nextState)
    await dispatch(initPointCardAction())
    dispatch(push('/'))
  }

  const handleOpenConfirmModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '削除する',
        'キャンセル',
        `お客さまが取得したポイントカードが使えなくなります。\n<span style="color: red">※すでにお客さまが取得した特典は削除されません。</span>`,
        handleDeletePointCard,
        `<p style="text-align: left">削除します</p>`
      )
    )
  }, [handleDeletePointCard])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      {pointCard && (
        <>
          {pointCard.status === 'suspended' && pointCardOffers.length === 0 && (
            <>
              <AlertCard
                description={'特典が紐づいていないため、カードを公開できません。最低1つ以上の特典を設定してください。'}
                headline={'カードを公開できません'}
                title={'注意'}
              />
              <Spacer size={40} />
            </>
          )}
          <PointCardPreviewCard pointCard={pointCard} />
        </>
      )}
      <Spacer size={40} />
      {pointCard && (
        <PointCardCard
          onPublish={handlePublishPointCard}
          onSuspend={handleSuspendPointCard}
          pointCard={pointCard}
          pointCardOffers={pointCardOffers}
          pointCardUsersAmount={pointCardUsers.length}
        />
      )}
      <Spacer size={40} />
      {pointCard && (
        <>
          <BlueButton label={'カード情報を編集する'} onClick={handleEdit} size={'medium'} type={'main'} />
          <Spacer size={24} />
          <CautionButton label={'削除する'} onClick={handleOpenConfirmModal} size={'medium'} type={'outlined'} />
          <Spacer size={24} />
        </>
      )}
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={40} />
    </div>
  )
}

export default PointCardTemplate
